import React, { useEffect } from "react";
import { Flex, Box } from "theme-ui";
import { markDispatchNotesRead } from "api/Projects";
import P from "components/typography/P";
import { themevals as theme, themevals } from "theme/themevals";

type NotesProps = {
  dispatch?: any;
  onDispatchViewed?: () => Promise<void> | void;
};

const Notes: React.FC<NotesProps> = ({ dispatch, onDispatchViewed }) => {
  const notes = Array.isArray(dispatch?.notes) ? dispatch.notes : [];
  const hasUnreadNotes = dispatch?.driverNotesRead !== true && notes.length > 0;

  useEffect(() => {
    if (!dispatch?.id || !hasUnreadNotes) {
      return;
    }

    let cancelled = false;

    const markNotesAsRead = async () => {
      try {
        await markDispatchNotesRead({ id: dispatch.id });
        if (!cancelled && typeof onDispatchViewed === "function") {
          await onDispatchViewed();
        }
      } catch (error) {
        console.error("Failed to update driver notes status", error);
      }
    };

    markNotesAsRead();

    return () => {
      cancelled = true;
    };
  }, [dispatch?.id, hasUnreadNotes, onDispatchViewed]);

  const formatTimestamp = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const resolveAuthor = (note: any) => {
    const first = note?.lastUpdateBy?.firstName;
    const last = note?.lastUpdateBy?.lastName;
    const name = [first, last].filter(Boolean).join(" ").trim();
    if (name) {
      return name;
    }
    return note?.lastUpdateBy?.email || "Team Member";
  };

  if (!notes.length) {
    return (
      <Flex
        sx={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Rubik",
          fontSize: "14px",
          color: "#6B7280",
          textAlign: "center",
          paddingTop: "40px",
          borderRadius: "6px", border: `1px solid ${themevals.colors.mdgrey}`, boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)"
        }}
      >
        There are no notes for this dispatch.
      </Flex>
    );
  }

  return (
    <Flex sx={{ flexDirection: "column", gap: "10px", marginTop: "20px" }}>
      {notes.map((note: any, index: number) => {
        const author = resolveAuthor(note);
        const timestamp = formatTimestamp(note?.createdAt);
        return (
          <Box
            key={note?.id || index}
            sx={{
              background: "#ffffff",
              borderRadius: "6px",
              padding: "16px",
              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
              border: `1px solid ${themevals.colors.mdgrey}`
            }}
          >
            <P
              fontFamily="Poppins"
              fontSize="14px"
              lineHeight="22px"
              marginTop="0"
              marginBottom="0"
            >
              {note?.note || "-"}
            </P>
            {(author || timestamp) && (
              <Flex
                sx={{
                  marginTop: "12px",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  fontFamily: "Rubik",
                  color: "#6B7280",
                }}
              >
                <span>{author}</span>
                <span>{timestamp}</span>
              </Flex>
            )}
          </Box>
        );
      })}
    </Flex>
  );
};

export default Notes;
